import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Select,
  Modal,
  EMOJI_COLOR_CLASSES,
} from '@/components/ui';
import { ChangeProfileImage } from '@/components/domain/shared/ChangeProfileImage';
import { ProfileAvatar } from '@/components/domain/shared/ProfileAvatar';
import { SelectEmoji } from '@/components/domain/shared/SelectEmoji';
import { SettingsSubPage } from '@/pages/settings/SettingsSubPage';
import { useAuth } from '@/hooks';
import { useUserStore } from '@/stores/userStore';
import { useHouseholdStore } from '@/stores/householdStore';
import { toast } from '@/stores';
import { userService, profileImageService } from '@/services';
import {
  ROLE_OPTIONS,
  CUSTOM_ROLE_SENTINEL,
  isKnownRoleValue,
} from '@/constants/roles';
import { getAccessLevelLabel } from '@/constants/accessLevels';
import {
  DEFAULT_AVATAR_EMOJI,
  googleAvatarUrlFrom,
  ownAvatarProps,
} from '@/lib/ownAvatar';
import { nameError, normalizeName } from '@/lib/nameField';

const DELETE_REASONS = [
  "It didn't work with my email setup",
  "I didn't find it useful",
  "I'm using a different app",
  'It was too expensive',
  'I have privacy concerns',
  "I'd rather not say",
];

function getTimezoneOptions(): Array<{ value: string; label: string }> {
  try {
    const zones = Intl.supportedValuesOf('timeZone');
    return zones.map((tz) => ({ value: tz, label: tz.replace(/_/g, ' ') }));
  } catch {
    // Fallback for environments without supportedValuesOf
    const fallbackZones = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'Pacific/Honolulu',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Asia/Shanghai',
      'Australia/Sydney',
    ];
    return fallbackZones.map((tz) => ({
      value: tz,
      label: tz.replace(/_/g, ' '),
    }));
  }
}

interface SavedState {
  name: string;
  role: string;
  timezone: string;
}

/** Split a persisted users.role text into the Role Select pair. */
function splitRoleValue(role: string | null): {
  roleSelect: string;
  customRole: string;
} {
  if (!role) return { roleSelect: '', customRole: '' };
  if (isKnownRoleValue(role)) return { roleSelect: role, customRole: '' };
  // Anything not in the canonical list (legacy "Grandparent", "Other", a
  // free-text value from onboarding, …) routes into the custom input so
  // the user sees what was actually saved.
  return { roleSelect: CUSTOM_ROLE_SENTINEL, customRole: role };
}

export function SettingsProfile() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const {
    userId,
    displayName,
    avatarEmoji,
    avatarColor: storeAvatarColor,
    avatarImagePath: storeAvatarImagePath,
    timezone: storeTimezone,
    profileRole: storeProfileRole,
    email,
    role: accessRole,
  } = useUserStore();
  const updateProfileStore = useUserStore((s) => s.updateProfile);
  const activeHouseholdId = useHouseholdStore((s) => s.householdId);

  // Sub-screen states
  const [showChangeImage, setShowChangeImage] = useState(false);
  const [showSelectEmoji, setShowSelectEmoji] = useState(false);

  // Form state
  const [name, setName] = useState(displayName ?? '');
  const initialRoleSplit = splitRoleValue(storeProfileRole);
  const [roleSelect, setRoleSelect] = useState(initialRoleSplit.roleSelect);
  const [customRole, setCustomRole] = useState(initialRoleSplit.customRole);
  const [timezone, setTimezone] = useState(
    storeTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );

  // Avatar state: `null` means "DB has no value yet, show fallback for
  // display only". `avatarTouched` flips to true the moment the user opens
  // the emoji picker and saves — only then do we include avatar_emoji /
  // avatar_color in the DB payload, preventing the placeholder unicorn /
  // bg-emoji-1 from being stamped onto existing rows.
  const [emoji, setEmoji] = useState<string | null>(avatarEmoji);
  const [avatarColor, setAvatarColor] = useState<string | null>(
    storeAvatarColor,
  );
  const [avatarTouched, setAvatarTouched] = useState(false);

  // Saved state for dirty tracking (role/name/timezone only — avatar uses
  // avatarTouched).
  const [saved, setSaved] = useState<SavedState>({
    name: displayName ?? '',
    role: storeProfileRole ?? '',
    timezone: storeTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Loading state
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Delete flow
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState<string | null>(null);
  const [deleteOtherText, setDeleteOtherText] = useState('');

  // Timezone options
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const resolvedRole =
    roleSelect === CUSTOM_ROLE_SENTINEL ? customRole.trim() : roleSelect;

  // Sync from store on mount
  useEffect(() => {
    const split = splitRoleValue(storeProfileRole);
    setName(displayName ?? '');
    setRoleSelect(split.roleSelect);
    setCustomRole(split.customRole);
    setTimezone(
      storeTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    );
    setEmoji(avatarEmoji);
    setAvatarColor(storeAvatarColor);
    setAvatarTouched(false);
    setSaved({
      name: displayName ?? '',
      role: storeProfileRole ?? '',
      timezone:
        storeTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  }, [
    displayName,
    storeProfileRole,
    storeTimezone,
    avatarEmoji,
    storeAvatarColor,
  ]);

  // Shown only once the parent has changed the field, so an account that
  // already has a blank name is not greeted by an error it did not cause.
  const nameInvalid = nameError(name, 'Name');
  const shownNameError = name !== saved.name ? nameInvalid : null;

  const isDirty =
    name !== saved.name ||
    resolvedRole !== saved.role ||
    timezone !== saved.timezone ||
    avatarTouched;

  const handleSave = useCallback(async () => {
    if (nameError(name, 'Name')) return;
    const cleanName = normalizeName(name);
    setSaving(true);
    try {
      // Only include avatar_* when the user explicitly picked one this
      // session. Otherwise we'd overwrite NULL-in-DB rows with the
      // placeholder unicorn / bg-emoji-1.
      // The family role is per household (YAR-485): it goes to the active
      // household's membership, not to users.role.
      const updates: Parameters<typeof userService.updateProfile>[0] = {
        display_name: cleanName,
        timezone,
      };
      if (avatarTouched) {
        updates.avatar_emoji = emoji;
        updates.avatar_color = avatarColor;
      }
      if (resolvedRole !== saved.role) {
        if (!activeHouseholdId) throw new Error('No active household');
        await userService.setMyFamilyRole(activeHouseholdId, resolvedRole);
      }
      await userService.updateProfile(updates);
      updateProfileStore({
        displayName: cleanName,
        timezone,
        profileRole: resolvedRole,
        ...(avatarTouched && {
          avatarEmoji: emoji,
          avatarColor,
        }),
      });
      setName(cleanName);
      setSaved({ name: cleanName, role: resolvedRole, timezone });
      setAvatarTouched(false);
      // Figma's copy (Settings / profile (changed) / success, YAR-142).
      toast.success('User profile updated');
    } catch (err) {
      console.error('Failed to save profile:', err);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  }, [
    name,
    resolvedRole,
    timezone,
    emoji,
    avatarColor,
    avatarTouched,
    updateProfileStore,
    saved.role,
    activeHouseholdId,
  ]);

  const handleEmojiSave = useCallback((newEmoji: string, newColor: string) => {
    // Purely local — the Save Changes button persists to the DB.
    setEmoji(newEmoji);
    setAvatarColor(newColor);
    setAvatarTouched(true);
    setShowSelectEmoji(false);
    setShowChangeImage(false);
  }, []);

  const handleUploadImage = useCallback(
    async (file: File) => {
      if (!userId) throw new Error('User not loaded');
      const { avatar_image_path } = await profileImageService.uploadAvatarImage(
        'user',
        userId,
        file,
      );
      // Persist through the store so every subscriber (sidebar, today, etc.)
      // re-renders with the new image immediately.
      updateProfileStore({ avatarImagePath: avatar_image_path });
      setShowChangeImage(false);
      toast.success('Profile photo updated');
    },
    [userId, updateProfileStore],
  );

  const handleRemoveImage = useCallback(async () => {
    if (!userId) throw new Error('User not loaded');
    await profileImageService.removeAvatarImage('user', userId);
    updateProfileStore({ avatarImagePath: null });
    setShowChangeImage(false);
    toast.success('Profile photo removed');
  }, [userId, updateProfileStore]);

  const handleDeleteAccount = useCallback(async () => {
    setDeleting(true);
    try {
      const reason =
        deleteReason === 'Other...'
          ? deleteOtherText.trim() || undefined
          : (deleteReason ?? undefined);
      await userService.deleteAccount(reason);
      // Account deleted — sign out and redirect regardless of signOut result
      try {
        await signOut();
      } catch {
        // signOut may fail if session is already invalidated — that's fine
      }
      navigate('/');
    } catch (err) {
      console.error('Failed to delete account:', err);
      toast.error('Failed to delete account');
      setDeleting(false);
      setShowDeleteConfirmModal(false);
    }
  }, [deleteReason, deleteOtherText, signOut, navigate]);

  const handleOpenDeleteFlow = useCallback(() => {
    setDeleteReason(null);
    setDeleteOtherText('');
    setShowDeleteReasonModal(true);
  }, []);

  const handleReasonNext = useCallback(() => {
    setShowDeleteReasonModal(false);
    setShowDeleteConfirmModal(true);
  }, []);

  // --- Sub-screens render INSTEAD of the profile form ---

  if (showSelectEmoji) {
    return (
      <SelectEmoji
        currentEmoji={emoji ?? DEFAULT_AVATAR_EMOJI}
        currentColor={avatarColor ?? (EMOJI_COLOR_CLASSES[0] as string)}
        onSave={handleEmojiSave}
        // Back lands on the option picker it came from (AC11 of YAR-143),
        // which is still open underneath — see onChooseEmoji below.
        onBack={() => setShowSelectEmoji(false)}
        headerVariant="subpage"
      />
    );
  }

  if (showChangeImage) {
    return (
      <ChangeProfileImage
        onBack={() => setShowChangeImage(false)}
        // Keep the picker open under Select emoji. Closing it here sent the
        // emoji screen's back chevron straight to the Profile form, skipping
        // this screen (YAR-143 AC11). Saving an emoji closes both.
        onChooseEmoji={() => setShowSelectEmoji(true)}
        headerVariant="subpage"
        onUpload={handleUploadImage}
        onRemove={handleRemoveImage}
        hasImage={!!storeAvatarImagePath}
        // Resolve through ownAvatarProps, exactly as the avatar on the form
        // behind this sheet does (YAR-302). The old inline preview defaulted
        // `emoji` to DEFAULT_AVATAR_EMOJI, which destroyed the very signal
        // that lets the Google photo stand in: a null `emoji` means "never
        // picked one". So a Google-photo user tapped their face and got the
        // unicorn.
        preview={{
          ...ownAvatarProps({
            avatarImagePath: storeAvatarImagePath,
            avatarEmoji: emoji,
            googleAvatarUrl: googleAvatarUrlFrom(user),
          }),
          color: avatarColor,
        }}
      />
    );
  }

  // --- Main profile form ---

  const deleteReasonFinal =
    deleteReason === 'Other...' ? deleteOtherText.trim() : deleteReason;
  const isDeleteDisabled = !deleteReasonFinal;

  return (
    <SettingsSubPage title="Profile">
      <div className="ph-no-capture px-5 py-6 flex flex-col gap-6">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => setShowChangeImage(true)}
            className="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary rounded-full"
            aria-label="Change profile image"
          >
            {/* Renders LOCAL state, not the store, so an emoji picked in this
                session shows before it is saved — hence ownAvatarProps() here
                rather than useOwnAvatar(). `emoji` is null until the user
                picks one, which is exactly the signal that lets the Google
                photo stand in as the default. */}
            <ProfileAvatar
              {...ownAvatarProps({
                avatarImagePath: storeAvatarImagePath,
                avatarEmoji: emoji,
                googleAvatarUrl: googleAvatarUrlFrom(user),
              })}
              color={avatarColor}
              size="2xl"
            />
          </button>
          <p className="text-sm text-muted-foreground">Tap to change image</p>
        </div>

        {/* Name */}
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. John Smith"
          error={shownNameError ?? undefined}
        />

        {/* Email (read-only info) */}
        <Input
          label="Email"
          value={email ?? ''}
          disabled
        />

        {/* Access Level — derived from household_members.role, never a
            literal. "Full access" was hardcoded here, so an Editor or a
            Viewer read their own access as full, and the string itself
            predates the official vocabulary ("Can edit · Can view"). */}
        <Input
          label="Access Level"
          value={getAccessLevelLabel(accessRole)}
          disabled
        />

        {/* Role */}
        <Select
          label="Role"
          placeholder="Select a role"
          value={roleSelect}
          onChange={(e) => setRoleSelect(e.target.value)}
          options={ROLE_OPTIONS}
        />
        {roleSelect === CUSTOM_ROLE_SENTINEL && (
          <Input
            label="Your role"
            value={customRole}
            onChange={(e) => setCustomRole(e.target.value)}
            placeholder="e.g. Grandma, Uncle..."
          />
        )}

        {/* Timezone */}
        <Select
          label="Timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          options={timezoneOptions}
        />

        {/* Save changes belongs to the FORM, not to the account-actions
            group: on Figma 7756:46443 it is the last child of the form
            frame, sitting 20px under the Timezone dropdown, while Sign out
            and Delete account are a separate block 52px further down.
            Shipping all three as one trio read as "three equal choices"
            and detached Save from the fields it saves (YAR-230).
            `h-11` pins the 44px height Figma draws for every button on
            this screen — `size="lg"` alone gives 20px type but a 46px box. */}
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className="h-11"
          disabled={!isDirty || !!nameInvalid}
          loading={saving}
          onClick={handleSave}
        >
          Save changes
        </Button>

        {/* Account actions. `pt-7` on top of the column's own gap-6 is the
            52px Figma leaves between Save changes and Sign out. */}
        <div className="flex flex-col gap-3 pt-7">
          {/* Neutral, not the green `outline` button: Figma draws a white
              pill with a #5e5e5e border and a #5e5e5e label, and `outline`
              gave it the primary-hover border (#1f372d) and a primary green
              label. `secondary` supplies the white fill and the grey label;
              only the border needs overriding, since #5e5e5e has no border
              token of its own (border-strong is #d1d1d1). Same treatment as
              the onboarding SenderFooter button (YAR-161). */}
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            className="!border-muted-foreground h-11"
            onClick={signOut}
          >
            Sign out
          </Button>

          {/* Inverted on Figma relative to what shipped: white fill with an
              #a82222 label and border, not a solid red slab. That is exactly
              the `destructiveOutline` variant. */}
          <Button
            variant="destructiveOutline"
            size="lg"
            fullWidth
            className="h-11"
            onClick={handleOpenDeleteFlow}
          >
            Delete Account
          </Button>
        </div>
      </div>

      {/* Step 1: Delete reason modal */}
      <Modal
        open={showDeleteReasonModal}
        onClose={() => setShowDeleteReasonModal(false)}
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 p-5">
          <p className="text-xl font-medium text-foreground text-center w-full">
            Before you go, why are you leaving?
          </p>

          {DELETE_REASONS.map((reason) => (
            <button
              key={reason}
              type="button"
              onClick={() => setDeleteReason(reason)}
              className="flex gap-3 items-center w-full cursor-pointer"
            >
              <span
                className={[
                  'shrink-0 w-5 h-5 rounded-full border',
                  deleteReason === reason ? 'border-primary' : 'border-border',
                  'inline-flex items-center justify-center',
                ].join(' ')}
              >
                {deleteReason === reason && (
                  <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </span>
              <span className="text-base text-foreground text-left">
                {reason}
              </span>
            </button>
          ))}

          {/* Other option with inline input */}
          <div className="flex gap-3 items-center w-full">
            <button
              type="button"
              onClick={() => setDeleteReason('Other...')}
              className="shrink-0 cursor-pointer"
            >
              <span
                className={[
                  'w-5 h-5 rounded-full border',
                  deleteReason === 'Other...'
                    ? 'border-primary'
                    : 'border-border',
                  'inline-flex items-center justify-center',
                ].join(' ')}
              >
                {deleteReason === 'Other...' && (
                  <span className="w-2.5 h-2.5 rounded-full bg-primary" />
                )}
              </span>
            </button>
            <input
              type="text"
              placeholder="Other..."
              value={deleteOtherText}
              onFocus={() => setDeleteReason('Other...')}
              onChange={(e) => {
                setDeleteReason('Other...');
                setDeleteOtherText(e.target.value);
              }}
              className="flex-1 h-10 px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-text-placeholder focus:outline-none focus:border-primary"
            />
          </div>

          {/* This step only advances to the confirmation, it deletes
              nothing, so the button reads "Continue" and wears the primary
              style (#264133 fill, #1f372d border), not the red
              `destructiveOutline` — that tone belongs to the real Delete
              button on the next modal (YAR-144 AC3, Figma updated Sep 1).
              Disabled is `disabled-bg` + `disabled-text` (#999999) from the
              shared Button, not the #5e5e5e that shipped (YAR-230). */}
          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="primary"
              fullWidth
              disabled={isDeleteDisabled}
              onClick={handleReasonNext}
            >
              Continue
            </Button>
            <Button
              variant="tertiary"
              fullWidth
              onClick={() => setShowDeleteReasonModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Step 2: Delete confirmation modal */}
      <Modal
        open={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        bare
      >
        <div className="ph-no-capture flex flex-col gap-4 items-center p-5">
          {/* Copy and tones are AC5/AC6 of YAR-144, re-checked against Figma
              in ybrito's Aug 24 pass: the title carries the heading tone
              #1a1a1a (`text-text-heading`), not the #2f2f2f body tone, and
              the body carries #2f2f2f (`text-text-body`), not the #5e5e5e
              `muted-foreground` that shipped. */}
          <p className="text-xl font-medium text-text-heading text-center w-full">
            Are you sure you want to delete your account?
          </p>
          <p className="text-sm text-text-body text-center leading-5">
            If you&apos;re stepping away, that&apos;s okay. We will keep your
            data for <span className="font-semibold">30 days</span>, then
            permanently delete it.
          </p>
          {/* Same treatment as the reason modal. The outline it already had
              was painted with `danger` (#c94040), which maps to no Figma
              variable; the error tone is #a82222, and `destructiveOutline`
              is the variant that carries it on both the label and the
              border. */}
          <div className="flex flex-col gap-3 w-full">
            <Button
              variant="destructiveOutline"
              fullWidth
              disabled={deleting}
              onClick={handleDeleteAccount}
            >
              {deleting ? 'Deleting...' : 'Delete account'}
            </Button>
            <Button
              variant="tertiary"
              fullWidth
              onClick={() => setShowDeleteConfirmModal(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </SettingsSubPage>
  );
}
